// react_app/src/components/admin/translations/TranslationRow.tsx

import { useState, useRef, useEffect } from 'react';
import { Check, Loader2, AlertCircle, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAlert } from '../../../contexts/AlertContext';
import { translationApi, type LanguageTextDto } from '../../../api/translationService';
import { invalidateCachedTranslations } from '../../../i18n/translationCache';

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

interface TranslationRowProps {
  text: LanguageTextDto;
  cultureName: string;
  onUpdated: (name: string, newValue: string) => void;
}

export function TranslationRow({ text, cultureName, onUpdated }: TranslationRowProps) {
  const { t } = useTranslation();
  const { confirm } = useAlert();
  const [value, setValue] = useState(text.value || '');
  const [status, setStatus] = useState<RowStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const originalValue = useRef(text.value || '');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setValue(text.value || '');
    originalValue.current = text.value || '';
    setStatus('idle');
  }, [text.value, text.name]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, []);

  const handleBlur = async () => {
    const trimmed = value.trim();
    if (trimmed === originalValue.current) return;

    if (trimmed === '' && originalValue.current !== '') {
      const confirmed = await confirm({
        message: t('App.Admin.Translations.ClearConfirm'),
        variant: 'danger',
      });
      if (!confirmed) {
        setValue(originalValue.current);
        return;
      }
    }

    setStatus('saving');
    setErrorMsg('');
    try {
      await translationApi.updateLanguageText(cultureName, text.name, trimmed);
      await invalidateCachedTranslations(cultureName);
      originalValue.current = trimmed;
      onUpdated(text.name, trimmed);
      setStatus('saved');
      savedTimerRef.current = setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('error');
      setErrorMsg(t('App.Admin.Translations.SaveError'));
    }
  };

  const handleRestore = async () => {
    const confirmed = await confirm({
      message: t('App.Admin.Translations.RestoreConfirm'),
    });
    if (!confirmed) return;

    setStatus('saving');
    try {
      await translationApi.restoreLanguageText(cultureName, text.name);
      await invalidateCachedTranslations(cultureName);
      setValue('');
      originalValue.current = '';
      onUpdated(text.name, '');
      setStatus('idle');
    } catch {
      setStatus('error');
      setErrorMsg(t('App.Admin.Translations.RestoreError'));
    }
  };

  return (
    <tr className="border-b border-neutral-100 hover:bg-neutral-50/50">
      <td className="px-4 py-2 font-mono text-xs text-neutral-500 max-w-[280px] truncate" title={text.name}>
        {text.name}
      </td>
      <td className="px-4 py-2 text-sm text-neutral-600 max-w-[300px]">
        <span className="line-clamp-2">{text.baseValue}</span>
      </td>
      <td className="px-4 py-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          className={`w-full px-2 py-1.5 text-sm border rounded-md transition-colors ${
            status === 'error'
              ? 'border-error-300 bg-error-50'
              : status === 'saved'
              ? 'border-success-300 bg-success-50'
              : 'border-neutral-200 bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400'
          }`}
          placeholder={text.baseValue}
        />
      </td>
      <td className="px-4 py-2 w-10 text-center">
        {status === 'saving' && <Loader2 className="w-4 h-4 animate-spin text-neutral-400 mx-auto" />}
        {status === 'saved' && <Check className="w-4 h-4 text-success-500 mx-auto" />}
        {status === 'error' && (
          <span title={errorMsg}>
            <AlertCircle className="w-4 h-4 text-error-500 mx-auto" />
          </span>
        )}
      </td>
      <td className="px-4 py-2 w-10">
        {originalValue.current !== '' && (
          <button
            onClick={handleRestore}
            className="p-1 text-neutral-400 hover:text-warning-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            title={t('App.Admin.Translations.Restore')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
}
